import React, { Ref, useEffect, useRef, useState } from "react";
import Image from "gatsby/image";
import * as styles from "./PreviewOverlay.module.css";
interface Props {}

type DeployData = {
  errorMessage?: string | null;
  state: string;
  updatedAt: string;
};
export const PreviewOverlay: React.FC<Props> = () => {
  const [buildInProgress, setBuildInProgress] = useState<boolean | null>(null);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [activeBuildTooltip, setActiveBuildTooltip] =
    useState<HTMLDivElement | null>(null);
  const previewTipRef = useRef<HTMLDivElement | null>(null);
  const prodTipRef = useRef<HTMLDivElement | null>(null);
  const complianceTipRef = useRef<HTMLDivElement | null>(null);
  const copyLinkRef = useRef<HTMLDivElement | null>(null);
  const [latestDeployData, setLatestDeployData] = useState<DeployData | null>();

  const buildIndicatorColors = {
    new: "#d8ab50",
    pending_review: "#d8ab50",
    accepted: "#d8ab50",
    rejected: "#fc4f38",
    enqueued: "#d8ab50",
    building: "#d8ab50",
    uploading: "#d8ab50",
    uploaded: "#d8ab50",
    preparing: "#d8ab50",
    prepared: "#d8ab50",
    processing: "#d8ab50",
    processed: "#d8ab50",
    ready: "#9ee6e8",
    error: "#fc4f38",
    retrying: "#d8ab50",
  };

  useEffect(() => {
    if (buildInProgress && activeBuildTooltip) {
      const initialInnerText =
        activeBuildTooltip.children[0].children[0].innerText;

      activeBuildTooltip.children[0].children[0].innerText =
        "Build triggered ✨";
      activeBuildTooltip.style.opacity = "1";

      setTimeout(() => {
        activeBuildTooltip.style.removeProperty("opacity");
        activeBuildTooltip.children[0].children[0].innerText = initialInnerText;
        setActiveBuildTooltip(null);
        setBuildInProgress(false);
      }, 5000);
    }

    if (buildError && activeBuildTooltip) {
      const initialInnerText =
        activeBuildTooltip.children[0].children[0].innerText;

      activeBuildTooltip.children[0].children[0].innerText = buildError;
      activeBuildTooltip.style.opacity = "1";

      setTimeout(() => {
        activeBuildTooltip.style.removeProperty("opacity");
        activeBuildTooltip.children[0].children[0].innerText = initialInnerText;
        setActiveBuildTooltip(null);
        setBuildError(null);
      }, 2000);
    }
  }, [buildInProgress, buildError]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetch(
        "https://api.netlify.com/api/v1/sites/48dec931-5098-4d55-a757-0c3864982818/deploys?page=1&per_page=1",
        {
          headers: {
            Authorization: `Bearer ${process.env.GATSBY_NETLIFY_ACCESS_TOKEN}`,
            "Access-Control-Allow-Origin": "*",
          },
        }
      )
        .then((resp: any) => resp.json())
        .then((data) => {
          const newDeployData = {
            errorMessage: data[0]?.error_message,
            state: data[0]?.state,
            updatedAt: data[0]?.updated_at,
          };

          newDeployData.updatedAt !== latestDeployData?.updatedAt &&
            setLatestDeployData(newDeployData);
        });
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const refreshCache = async (environment: string) => {
    switch (environment) {
      case "prod":
        setActiveBuildTooltip(prodTipRef.current);
        break;
      case "preview":
        setActiveBuildTooltip(previewTipRef.current);
        break;
      case "compliance":
        setActiveBuildTooltip(complianceTipRef.current);
        break;
    }

    try {
      await fetch(`/api/refresh/${environment}`, {
        method: "post",
        headers: {
          "Access-Control-Allow-Origin": "*",
        },
      }).then((response) => {
        response.status === 200
          ? setBuildInProgress(true)
          : setBuildInProgress(false);

        response.status === 429 &&
          setBuildError("Too many requests. Please try again in 15 minutes.");
      });
    } catch (error) {
      setBuildInProgress(false);
      setBuildError(`Build request failed.`);
    }
  };

  const handleCopyLinkClick = (event: React.MouseEvent) => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      const tooltip = copyLinkRef.current;
      const initialInnerText = tooltip?.children[0].innerText;

      if (tooltip?.children) {
        tooltip.children[0].innerText = "✅ Link copied";
        tooltip.style.opacity = "1";

        setTimeout(() => {
          tooltip?.style.removeProperty("opacity");
          tooltip.children[0].innerText = initialInnerText;
        }, 5000);
      }
    });
  };

  return (
    <div className={styles.previewOverlay}>
      <div className={styles.logo}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 128 113"
        >
          <g clipPath="url(#a)">
            <path
              fill="#05BDBA"
              d="M34.593 94.05h-1.209l-6.033-6.032v-1.209l9.223-9.222h6.39l.852.852v6.39l-9.223 9.222Zm-7.242-68.234v-1.209l6.033-6.033h1.209l9.223 9.223v6.39l-.852.852h-6.39l-9.223-9.223Z"
            />
            <path
              fill="#014847"
              d="M80.46 74.605h-8.778l-.733-.733V53.326c0-3.656-1.436-6.489-5.844-6.588-2.269-.06-4.865 0-7.638.11l-.416.425v26.589l-.733.733H47.54l-.733-.733V38.764l.733-.733h19.753c7.677 0 13.898 6.22 13.898 13.898v21.943l-.733.733Z"
            />
            <path
              fill="#05BDBA"
              d="M35.841 61.45H.733L0 60.715v-8.797l.733-.733h35.108l.733.733v8.797l-.733.733Zm91.436 0H92.169l-.733-.734v-8.797l.733-.733h35.108l.733.733v8.797l-.733.733ZM58.943 27.064V.734L59.676 0h8.797l.733.733v26.331l-.733.733h-8.797l-.733-.733Zm0 84.838v-26.33l.733-.734h8.797l.733.733v26.331l-.733.733h-8.797l-.733-.733Z"
            />
          </g>
          <defs>
            <clipPath id="a">
              <path fill="#fff" d="M0 0h128v112.635H0z" />
            </clipPath>
          </defs>
        </svg>
      </div>
      <div className={styles.badge}>
        <div className={styles.button}>
          <button
            style={{
              color: buildIndicatorColors[latestDeployData?.state],
            }}
            className={styles.indicator}
          >
            ⬤
          </button>
          <div className={styles.tooltip}>
            <div className={styles.tooltipText}>
              Web Spotlight Build Status:{" "}
              {latestDeployData?.state?.toUpperCase()}. Updated at{" "}
              {new Date(latestDeployData?.updatedAt)?.toLocaleString()}.
            </div>
          </div>
        </div>
      </div>
      <div className={styles.button}>
        <button onClick={handleCopyLinkClick}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M9 15l6 -6" />
            <path d="M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464" />
            <path d="M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463" />
          </svg>
        </button>
        <div className={styles.tooltip} ref={copyLinkRef}>
          <div className={styles.tooltipText}>Copy link</div>
        </div>
      </div>

      <div className={styles.button}>
        <button onClick={() => refreshCache("prod")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0" />
            <path d="M3.6 9h16.8" />
            <path d="M3.6 15h16.8" />
            <path d="M11.5 3a17 17 0 0 0 0 18" />
            <path d="M12.5 3a17 17 0 0 1 0 18" />
          </svg>
        </button>
        <div className={styles.tooltip} ref={prodTipRef}>
          <div className={styles.tooltipText}>
            <div>Clear production site cache and rebuild</div>
            <img src="https://api.netlify.com/api/v1/badges/12787199-cffc-4e61-b445-2a519efe865f/deploy-status" />
          </div>
        </div>
      </div>
      <div className={styles.button}>
        <button onClick={() => refreshCache("preview")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M10 12a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
            <path d="M21 12c-2.4 4 -5.4 6 -9 6c-3.6 0 -6.6 -2 -9 -6c2.4 -4 5.4 -6 9 -6c3.6 0 6.6 2 9 6" />
          </svg>
        </button>
        <div className={styles.tooltip} ref={previewTipRef}>
          <div className={styles.tooltipText}>
            <div>Clear Web Spotlight site cache and rebuild</div>
            <img src="https://api.netlify.com/api/v1/badges/48dec931-5098-4d55-a757-0c3864982818/deploy-status" />
          </div>
        </div>
      </div>
      <div className={styles.button}>
        <button onClick={() => refreshCache("compliance")}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path stroke="none" d="M0 0h24v24H0z" fill="none" />
            <path d="M13 10l7.383 7.418c.823 .82 .823 2.148 0 2.967a2.11 2.11 0 0 1 -2.976 0l-7.407 -7.385" />
            <path d="M6 9l4 4" />
            <path d="M13 10l-4 -4" />
            <path d="M3 21h7" />
            <path d="M6.793 15.793l-3.586 -3.586a1 1 0 0 1 0 -1.414l2.293 -2.293l.5 .5l3 -3l-.5 -.5l2.293 -2.293a1 1 0 0 1 1.414 0l3.586 3.586a1 1 0 0 1 0 1.414l-2.293 2.293l-.5 -.5l-3 3l.5 .5l-2.293 2.293a1 1 0 0 1 -1.414 0z" />
          </svg>
        </button>
        <div className={styles.tooltip} ref={complianceTipRef}>
          <div className={styles.tooltipText}>
            <div>Clear Compliance Preview site cache and rebuild</div>
            <img src="https://api.netlify.com/api/v1/badges/3ebf70a0-59fa-43b4-81fd-5dfade48a7f5/deploy-status" />
          </div>
        </div>
      </div>
    </div>
  );
};
